import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowLeft, FileText, ShieldCheck } from "lucide-react";
import { Spinner } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";

function QuotationBubble({ meta, mine, onPay, onDecline }) {
  return (
    <div className="bg-white rounded-2xl border border-black/10 shadow-soft p-4 w-72 max-w-full" data-testid="quotation-bubble">
      <div className="font-heading font-bold text-navy text-sm mb-2">💰 Quotation</div>
      <div className="space-y-1 text-sm">
        {[["Product", meta.product_price], ["Service fee", meta.service_fee], ["Platform fee", meta.platform_fee], ["Delivery", meta.delivery_fee]].map(([l, v]) => (
          <div key={l} className="flex justify-between text-muted-foreground"><span>{l}</span><span className="text-ink">{rupiah(v)}</span></div>
        ))}
        <div className="flex justify-between font-bold text-navy pt-2 border-t border-black/10 mt-1"><span>Total</span><span>{rupiah(meta.total)}</span></div>
      </div>
      {!mine && onPay && (
        <div className="flex gap-2 mt-3">
          <button onClick={onPay} data-testid="quote-pay-btn" className="flex-1 rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-2 text-xs transition-colors">Accept & Pay</button>
          <button onClick={onDecline} data-testid="quote-decline-btn" className="rounded-full bg-white border border-black/10 text-navy font-bold px-3 py-2 text-xs">Decline</button>
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const { convId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState({ product_price: "", service_fee: "", delivery_fee: "0" });
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadMsgs = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/conversations/${convId}/messages`);
      setMsgs(data);
    } catch { /* ignore */ }
  }, [convId]);

  useEffect(() => {
    api.get(`/chat/conversations/${convId}`).then((r) => setConv(r.data)).catch(() => navigate("/messages"));
    loadMsgs().finally(() => setLoading(false));
    const id = setInterval(loadMsgs, 4000);
    return () => clearInterval(id);
  }, [convId, loadMsgs, navigate]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post("/chat/messages", { conversation_id: convId, type: "text", text: t }); await loadMsgs(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const isBawainer = user?.role === "bawainer";
  const request = conv?.request;

  const sendQuote = async (e) => {
    e.preventDefault();
    if (!quote.product_price || !quote.service_fee) return toast.error("Isi harga produk & biaya jasa");
    setBusy(true);
    try {
      await api.post("/quotations", {
        request_id: request.id, product_price: Number(quote.product_price),
        service_fee: Number(quote.service_fee), delivery_fee: Number(quote.delivery_fee || 0),
      });
      toast.success("Quotation terkirim");
      setQuoteOpen(false); await loadMsgs();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const payQuote = async (quotationId) => {
    try {
      const { data } = await api.post("/orders/pay", { quotation_id: quotationId });
      toast.success("Pembayaran berhasil! Dana ditahan aman.");
      await loadMsgs();
      navigate(`/orders/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const declineQuote = async (quotationId) => {
    try { await api.post(`/quotations/${quotationId}/decline`); toast("Quotation ditolak"); await loadMsgs(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-9rem)] lg:h-[calc(100vh-8rem)]">
      {/* header */}
      <div className="flex items-center gap-3 bg-white rounded-2xl p-3 shadow-soft border border-black/5">
        <button onClick={() => navigate("/messages")} data-testid="chat-back"><ArrowLeft className="text-navy" size={20} /></button>
        <img src={conv?.other?.avatar} alt="" className="w-10 h-10 rounded-xl object-cover" />
        <div className="flex-1">
          <div className="font-heading font-bold text-navy">{conv?.other?.name}</div>
          {request && <div className="text-xs text-muted-foreground">Re: {request.product_name}</div>}
        </div>
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3 no-scrollbar">
        {msgs.map((m) => {
          if (m.type === "system") {
            if (m.meta?.kind === "quotation") {
              const mine = isBawainer;
              return (
                <div key={m.id} className="flex justify-center">
                  <QuotationBubble meta={m.meta} mine={mine}
                    onPay={!isBawainer ? () => payQuote(m.meta.quotation_id) : undefined}
                    onDecline={!isBawainer ? () => declineQuote(m.meta.quotation_id) : undefined} />
                </div>
              );
            }
            return (
              <div key={m.id} className="flex justify-center">
                <span className="text-[11px] bg-sky-soft text-sky rounded-full px-3 py-1 font-medium flex items-center gap-1">
                  <ShieldCheck size={11} /> {m.text}
                </span>
              </div>
            );
          }
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div data-testid="chat-message" className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-navy text-white rounded-br-sm" : "bg-white text-ink border border-black/5 rounded-bl-sm"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* composer */}
      <div className="bg-white rounded-2xl p-2 shadow-soft border border-black/5">
        {isBawainer && request && (
          <button onClick={() => setQuoteOpen(true)} data-testid="open-quote-btn"
            className="w-full mb-2 rounded-full bg-lime-light text-navy font-bold py-2 text-sm flex items-center justify-center gap-1.5">
            <FileText size={15} /> Send Quotation
          </button>
        )}
        <form onSubmit={send} className="flex items-center gap-2">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Tulis pesan..." className="flex-1 bg-cloud rounded-full px-4 py-2.5 text-sm outline-none" />
          <button type="submit" data-testid="chat-send" className="w-10 h-10 rounded-full bg-lime hover:bg-lime-dark flex items-center justify-center transition-colors">
            <Send size={17} className="text-navy" />
          </button>
        </form>
      </div>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">Send Quotation</DialogTitle></DialogHeader>
          <form onSubmit={sendQuote} className="space-y-3">
            <div><label className="text-sm font-semibold text-ink">Product price (Rp)</label>
              <Input data-testid="quote-product-price" type="number" value={quote.product_price} onChange={(e) => setQuote({ ...quote, product_price: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Service fee (Rp)</label>
              <Input data-testid="quote-service-fee" type="number" value={quote.service_fee} onChange={(e) => setQuote({ ...quote, service_fee: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Delivery fee (Rp)</label>
              <Input data-testid="quote-delivery-fee" type="number" value={quote.delivery_fee} onChange={(e) => setQuote({ ...quote, delivery_fee: e.target.value })} className="rounded-xl mt-1" /></div>
            <p className="text-xs text-muted-foreground">Platform fee (komisi) dihitung otomatis dari biaya jasa.</p>
            <button type="submit" disabled={busy} data-testid="quote-submit"
              className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors disabled:opacity-60">
              {busy ? "..." : "Kirim Quotation"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
